import { Nullable } from 'src/types/nullable.type';
import create from 'zustand';
import { getInsertionBoundary } from './helpers/block/get-insertion-boundary';
import { generateBlockId } from './helpers/generate-block-id';
import {
  ActionSource,
  Block,
  Boundary,
  ContextMenuState,
  ImageBlockContent,
  JNode,
  Operation,
  Rect,
  RenderElement,
  Section,
  SectionGrid,
  Size,
  TextBlockContent,
  UserEditorAction,
} from './types';
import produce from 'immer';
import { DEFAULT_LAYOUT_CONFIG, LayoutConfig, LayoutManager } from './layout-manager';
import { sync } from './editor-sync';
import { jDocToSections } from './helpers/jnode/jdoc-to-sections';
import { updateJDocWithBlocks } from './helpers/jnode/update-jdoc-with-blocks';
import { updateLayoutManagerForAction } from './helpers/actions/update-layout-manager-for-action';
import { uploadFileToS3 } from 'src/common/helpers/file-upload';
import { isMutualActonPlanAction, MutualActionPlanAction } from './mutual-action-plans/types';
import { useMutualActionPlansContext } from './mutual-action-plans/store';
import { inverseOperations } from './helpers/operations/inverse-operations';

export type DragPosition = {
  x: number;
  y: number;
};

export type MousePosition = {
  clientX: number;
  clientY: number;
};

export type DraggableState = {
  position: DragPosition;
  isDragging: boolean;
};

type Coords = {
  x: number;
  y: number;
};

type StateVars = {
  layout: {
    sections: Section[];
    sectionGrids: SectionGrid[];
    sectionRects: Rect[];
    renderElements: RenderElement[];
    boundaries: Boundary[];
    layoutReady: boolean;
    innerActualWidth: Nullable<number>;
    innerActualHeight: Nullable<number>;
    layoutConfig: LayoutConfig;
  };
  journeyName: Nullable<string>;
  lastActionWasUndoOrRedo: boolean;
  jdoc: Nullable<JNode>;
  latestTrackableJdoc: Nullable<JNode>;
  latestTrackableSections: Nullable<Section[]>;
  undoStack: UserEditorAction[];
  redoStack: UserEditorAction[];
  selectedBlockId: Nullable<Block['id']>;
  currentSectionId: Nullable<Section['id']>;
  scrollToSectionId: Nullable<Section['id']>;
  selectionCoords: Nullable<Coords>;
  editorContentElement: Nullable<HTMLElement>;
  scrollToBlockId: Nullable<Block['id']>;
  contextMenuState: Nullable<ContextMenuState>;
  // _draggableId: Nullable<string>;
  // position: DragPosition;
  isDraggingNewBlock: boolean;
  insertionBoundary: Nullable<Boundary>;
  appendingSection: boolean;
  editorContentRect: Nullable<Rect>;
  outerAreaRect: Nullable<Rect>;
  innerAreaRect: Nullable<Rect>;
  canvasScrollPosition: { x: number; y: number };
  online: boolean;
  initialTitleBlockId: Nullable<Block['id']>;
};

type StateFns = {
  undo: () => void;
  redo: () => void;
  selectBlock: (blockId: Block['id'], coords?: Coords) => void;
  clearSelectedBlock: () => void;
  setCurrentSectionId: (sectionId: Section['id']) => void;
  setScrollToSectionId: (sectionId: Nullable<Section['id']>) => void;
  setEditorContentElement: (element: HTMLElement) => void;
  setScrollToBlockId: (blockId: Block['id']) => void;
  setCanvasScrollPosition: (x: number, y: number) => void;
  setContainerRects: (rects: { editorContentRect: Rect; outerAreaRect: Rect; innerAreaRect: Rect }) => void;
  setBlockContentSize: (blockId: Block['id'], size: Size) => void;
  setContextMenuState: (state: ContextMenuState) => void;
  clearContextMenuState: () => void;
  dispatchUserEditorAction: (
    userEditorAction: UserEditorAction,
    config?: { source?: ActionSource; undoable?: boolean; syncable?: boolean }
  ) => Promise<any>;
  initEditor: (journeyName: string, jDoc: JNode) => void;
  clearLastActionWasUndoOrRedo: () => void;
  resetEditor(): void;
  setOffline(): void;
  setOnline(): void;
  setInitialTitleBlockId(id: Nullable<Block['id']>): void;
};

type State = StateVars & StateFns;

export function createDefaultSection(titleBlockId: Block['id']): JNode {
  return {
    type: { name: 'section' },
    attributes: {
      id: generateBlockId(),
    },
    content: [
      {
        type: { name: 'block' },
        attributes: {
          id: titleBlockId,
          content: {
            type: 'text',
            value: '<h1></h1>',
          },
          layout: {
            spec: {
              type: 'calculated-height',
              width: '100%',
              height: null,
              minHeight: 0,
              contentSize: null,
            },
            verticalAlign: 'center',
          },
        },
        content: [],
      },
    ],
  };
}

function createInitialState(): StateVars {
  return {
    layout: {
      sections: [],
      sectionGrids: [],
      renderElements: [],
      sectionRects: [],
      boundaries: [],
      layoutReady: false,
      innerActualWidth: null,
      innerActualHeight: null,
      layoutConfig: DEFAULT_LAYOUT_CONFIG,
    },
    journeyName: null,
    jdoc: null,
    lastActionWasUndoOrRedo: false,
    latestTrackableJdoc: null,
    latestTrackableSections: null,
    undoStack: [],
    redoStack: [],
    selectedBlockId: null,
    currentSectionId: null,
    selectionCoords: null,
    scrollToBlockId: null,
    scrollToSectionId: null,
    editorContentElement: null,
    contextMenuState: null,
    isDraggingNewBlock: false,
    insertionBoundary: null,
    appendingSection: false,
    editorContentRect: null,
    outerAreaRect: null,
    innerAreaRect: null,
    canvasScrollPosition: { x: 0, y: 0 },
    online: true,
    initialTitleBlockId: null,
  };
}

const lm = new LayoutManager();
lm.addListener('update', () => {
  // console.log('lm update');
  const { outputs } = lm;
  if (!outputs) {
    return;
  }
  // console.log('lm update sections', lm.sections);
  useEditorStore.setState(
    produce(useEditorStore.getState(), (state) => {
      state.layout = {
        renderElements: lm.renderElements,
        sections: lm.sections,
        sectionGrids: lm.sectionGrids,
        sectionRects: outputs.sectionRects,
        boundaries: lm.boundaries,
        layoutReady: true,
        innerActualWidth: outputs.innerActualWidth,
        innerActualHeight: outputs.innerActualHeight,
        layoutConfig: lm.layoutConfig,
      };
    })
  );
});

export const useEditorStore = create<State>((set, get) => ({
  ...createInitialState(),
  undo: () => {
    const { undoStack, dispatchUserEditorAction } = get();
    // console.log('UNDO', undoStack);
    if (undoStack.length > 0) {
      const action = undoStack[undoStack.length - 1];
      set({ undoStack: undoStack.slice(0, undoStack.length - 1) });
      dispatchUserEditorAction(action, { source: 'undo' });
    }
  },
  redo: () => {
    const { redoStack, dispatchUserEditorAction } = get();
    // console.log('REDO', redoStack);
    if (redoStack.length > 0) {
      const action = redoStack[redoStack.length - 1];
      set({ redoStack: redoStack.slice(0, redoStack.length - 1) });
      dispatchUserEditorAction(action, { source: 'redo' });
    }
  },
  selectBlock: (blockId: string, coords?: Coords) => {
    set({ selectedBlockId: blockId, selectionCoords: coords || null });
  },
  clearSelectedBlock: () => {
    set({ selectedBlockId: null });
  },
  setEditorContentElement: (editorContentElement: HTMLElement) => {
    set({ editorContentElement });
  },
  setCurrentSectionId: (currentSectionId: string) => {
    set({ currentSectionId });
  },
  setScrollToSectionId: (scrollToSectionId: Nullable<string>) => {
    set({ scrollToSectionId });
  },
  setScrollToBlockId: (scrollToBlockId: string) => {
    set({ scrollToBlockId });
  },
  setCanvasScrollPosition: (x: number, y: number) => {
    set({ canvasScrollPosition: { x, y } });
  },
  setContainerRects: ({
    editorContentRect,
    outerAreaRect,
    innerAreaRect,
  }: {
    editorContentRect: Rect;
    outerAreaRect: Rect;
    innerAreaRect: Rect;
  }) => {
    set({ editorContentRect, outerAreaRect, innerAreaRect });
    const innerAreaWidth = innerAreaRect.width;
    const innerAreaHeight = editorContentRect.height;
    const outerAreaLeftOffset = innerAreaRect.x - outerAreaRect.x;
    const outerAreaRightOffset = outerAreaRect.x + outerAreaRect.width - innerAreaRect.x - innerAreaRect.width;
    lm.setLayoutInputs({
      innerAreaWidth,
      innerAreaHeight,
      outerAreaLeftOffset,
      outerAreaRightOffset,
    });
  },
  setBlockContentSize: (blockId: string, size: Size) => {
    lm.setBlockContentSize(blockId, size);
  },
  setContextMenuState: (contextMenuState: ContextMenuState) => {
    set({ contextMenuState });
  },
  clearContextMenuState: () => {
    set({ contextMenuState: null });
  },
  dispatchUserEditorAction: (userEditorAction: UserEditorAction, config) => {
    const { undoable = true, syncable = true, source = 'none' } = config || {};
    let operations: Operation[] | undefined;

    let inverse = null;

    // console.log('dispatchUserEditorAction', userEditorAction, config);
    const { type } = userEditorAction;
    const {
      jdoc: oldJDoc,
      layout: oldLayout,
      undoStack,
      redoStack,
      latestTrackableSections,
      initialTitleBlockId,
      dispatchUserEditorAction,
    } = get();
    if (!oldJDoc || !latestTrackableSections) {
      console.warn('No jdoc');
      return Promise.reject('No jdoc');
    }
    let newJDoc: JNode;
    set({ lastActionWasUndoOrRedo: source === 'undo' || source === 'redo' });

    if (type === 'set-step-blocks') {
      newJDoc = produce(oldJDoc, (j) => {
        j.content = JSON.parse(userEditorAction.json);
      });
    } else if (type === 'update-block-content') {
      if (source === 'none') {
        maybeEditTitle(
          initialTitleBlockId,
          oldLayout.sections,
          userEditorAction.id,
          userEditorAction.content as TextBlockContent
        );
      }
    } else if (type === 'delete-block') {
      if (get().selectedBlockId === userEditorAction.id) {
        get().clearSelectedBlock();
      }
    } else if (type === 'replace-block') {
      if (userEditorAction.content.type === 'image') {
        if (!userEditorAction.content.url) {
          setTimeout(async () => {
            const content = userEditorAction.content as ImageBlockContent;
            const file = content.file;
            const response = await uploadFileToS3(file, 'neue-test');
            dispatchUserEditorAction(
              {
                type: 'set-image-url-after-upload',
                id: userEditorAction.id,
                url: response.url,
              },
              {
                undoable: false,
                syncable: true,
              }
            );
          }, 0);
        }
      }
    } else if (type === 'delete-section') {
      if (oldLayout.sections.length === 1 && oldLayout.sections[0].id === userEditorAction.id) {
        setTimeout(() => {
          dispatchUserEditorAction({
            type: 'append-section',
            id: generateBlockId(),
          });
        });
      }
    }
    let isActionValid = false;
    if (type === 'set-journey-name') {
      set({ journeyName: userEditorAction.name });
      isActionValid = true
    } else if (isMutualActonPlanAction(userEditorAction)) {
      isActionValid = true;
      inverse = useMutualActionPlansContext.getState().dispatch(userEditorAction as MutualActionPlanAction);
    } else {
      operations = updateLayoutManagerForAction(lm, userEditorAction, {
        sections: oldLayout.sections,
      });
      console.log('operations', operations);
      if (operations && operations.length > 0) {
        isActionValid = true;
        lm.applyOperations(operations);
      }
    }

    newJDoc = updateJDocWithBlocks(oldJDoc, get().layout.sections);

    if (undoable && isActionValid) {
      const oldState = source === 'undo' ? oldLayout.sections : latestTrackableSections;
      if (inverse === null) {
        if (operations) {
          inverse = inverseOperations(operations, oldState, lm.sections);
        }
      }

      if (inverse != null) {
        set({ latestTrackableSections: lm.sections });

        if (source === 'none') {
          set({ undoStack: [...undoStack, inverse], redoStack: [] });
        } else if (source === 'undo') {
          set({ redoStack: [...redoStack, inverse] });
        } else if (source === 'redo') {
          set({ undoStack: [...undoStack, inverse] });
        }
      }
    }

    set({ jdoc: newJDoc });

    if (syncable && isActionValid) {
      if (operations) {
        return sync({ type: 'apply-operations', operations }, oldLayout.sections, lm.sections, source);
      } else {
        return sync(userEditorAction, oldLayout.sections, lm.sections, source);
      }
    }

    return Promise.resolve();
  },
  clearLastActionWasUndoOrRedo: () => {
    set({ lastActionWasUndoOrRedo: false });
  },
  initEditor: (journeyName, jDoc) => {
    // console.log('initEditor', jDoc);
    const sections = jDocToSections(jDoc);
    lm.initializeSections(sections);
    set({ jdoc: jDoc, latestTrackableSections: sections, journeyName });
  },
  resetEditor() {
    set(createInitialState());
    lm.reset();
  },
  setOffline() {
    set({ online: false });
  },
  setOnline() {
    set({ online: true });
  },
  setInitialTitleBlockId(id) {
    set({ initialTitleBlockId: id });
  },
}));

// useEditorStore.subscribe((state) => {
//   console.log('useEditorStore changed', state);
// });

function maybeEditTitle(
  initialTitleBlockId: Nullable<Block['id']>,
  sections: Section[],
  editedBlockId: Block['id'],
  content: TextBlockContent
) {
  if (initialTitleBlockId === editedBlockId && sections.length === 1 && sections[0].blocks.length === 1) {
    const headingText = content?.valueJSON?.content[0]?.content[0]?.text?.slice(0, 80);
    if (headingText !== null) {
      setTimeout(() => {
        useEditorStore.getState().dispatchUserEditorAction(
          {
            type: 'set-journey-name',
            name: headingText,
          },
          {
            syncable: true,
            undoable: false,
          }
        );
      }, 0);
    }
  }
}

// @ts-ignore
window.editorStore = useEditorStore;
